import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { apiService, getFetchErrorMessage } from '../services/apiService';

interface User {
  id: string;
  userName: string;
  displayName: string;
}

interface Role {
  id: string;
  name: string;
}

interface UserRoleData {
  userId: string;
  roleId: string;
}

interface AddUserRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddUserRoleDialog: React.FC<AddUserRoleDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UserRoleData>({
    userId: '',
    roleId: '',
  });

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autocomplete states for users
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');

  // Load roles when dialog opens
  useEffect(() => {
    if (open) {
      loadRoles();
      // Reset form
      setFormData({ userId: '', roleId: '' });
      setSelectedUser(null);
      setUserOptions([]);
      setUserInputValue('');
      setError(null);
    }
  }, [open]);

  const loadRoles = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/roles`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Hiba történt a szerepek betöltése során.'));
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Hiba történt a szerepek betöltése során');
    }
  };

  // User search function
  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setUserOptions([]);
      return;
    }

    setUserLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/users/search?query=${encodeURIComponent(query)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUserOptions(data);
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Nem sikerült betölteni a felhasználókat.'));
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setError(error instanceof Error ? error.message : 'Nem sikerült betölteni a felhasználókat.');
    } finally {
      setUserLoading(false);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      roleId: event.target.value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedUser) {
      setError('Kérjük, válasszon felhasználót!');
      return;
    }
    if (!formData.roleId) {
      setError('Kérjük, válasszon szerepet!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        UserId: selectedUser.id,
        RoleId: formData.roleId,
      };

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/userroles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Hiba történt a felhasználói szerepkör hozzáadása során.'));
      }
    } catch (error: any) {
      console.error('Error adding user role:', error);
      setError(error.message || 'Hiba történt a felhasználói szerepkör hozzáadása során');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>Új felhasználói szerepkör hozzáadása</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* User Selection with Autocomplete */}
          <Autocomplete
            options={userOptions}
            getOptionLabel={(option) => option.displayName || option.userName}
            value={selectedUser}
            onChange={(_, newValue) => {
              setSelectedUser(newValue);
              setFormData(prev => ({
                ...prev,
                userId: newValue?.id || ''
              }));
            }}
            inputValue={userInputValue}
            onInputChange={(_, newInputValue) => {
              setUserInputValue(newInputValue);
              searchUsers(newInputValue);
            }}
            loading={userLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Felhasználó"
                placeholder="Kezdjen el gépelni a felhasználó kereséséhez..."
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {userLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              userInputValue.length < 2 
                ? "Írjon be legalább 2 karaktert a kereséshez" 
                : "Nincs találat"
            }
          />

          {/* Role Selection */}
          <FormControl fullWidth required>
            <InputLabel>Szerepkör</InputLabel>
            <Select
              value={formData.roleId}
              label="Szerepkör"
              onChange={handleRoleChange}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
        >
          Mégse
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedUser || !formData.roleId}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Hozzáadás...' : 'Hozzáadás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserRoleDialog;
